/**
 * Database Schema Type Definitions
 * 
 * This file contains all TypeScript interfaces for the booking management database schema.
 * These types are designed to work with sessionStorage but can be migrated to a backend database.
 * 
 * @version 1.0.0
 * @see DATABASE_SCHEMA_DESIGN.md for complete schema documentation
 */

import { 
  DurationType, 
  HalfDayPeriod, 
  RecurrenceSettings, 
  BillingClassification, 
  WorkType, 
  BookingStatus, 
  PriorityLevel,
  WorkspaceRole,
  FieldConfiguration,
  TabConfiguration
} from './index';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * Booking category classification
 */
export type BookingCategory = 
  | 'project'           // Project work
  | 'absence'           // Leave/vacation
  | 'training'          // Training/development
  | 'meeting'           // Meetings
  | 'internal'          // Internal work
  | 'maintenance'       // System maintenance
  | 'other';            // Other categories

/**
 * Approval workflow status
 */
export type ApprovalStatus = 
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled';

/**
 * Type of change made to a booking
 */
export type ChangeType = 
  | 'created'
  | 'updated'
  | 'deleted'
  | 'restored'
  | 'approved'
  | 'rejected'
  | 'status_changed'
  | 'rescheduled';

/**
 * Type of booking conflict
 */
export type ConflictType = 
  | 'time_overlap'
  | 'resource_unavailable'
  | 'double_booking'
  | 'capacity_exceeded';

/**
 * Severity level of a conflict
 */
export type ConflictSeverity = 
  | 'critical'                   // Must be resolved
  | 'warning'                    // Should be reviewed
  | 'info';                      // Informational only

/**
 * How a conflict was resolved
 */
export type ResolutionType = 
  | 'rescheduled'
  | 'cancelled'
  | 'approved_override'
  | 'resource_changed'
  | 'split_booking';

// ============================================================================
// BOOKING TYPES TABLE
// ============================================================================

/**
 * Booking Type Record
 * 
 * Stores booking type configurations and metadata.
 * This is the master configuration for different types of bookings.
 */
export interface BookingTypeRecord {
  // Primary Key
  id: string;                    // UUID format: 'bt-uuid-xxxxx'
  
  // Core Fields
  code: string;                  // Unique code (e.g., 'PROJECT', 'ABSENCE')
  name: string;                  // Display name (e.g., 'Project Booking')
  description?: string;          // Optional description
  category: BookingCategory;     // Category classification
  
  // Status & Visibility
  active: boolean;               // Is this booking type active?
  isDefault: boolean;            // Is this the default booking type?
  sortOrder: number;             // Display order (lower = higher priority)
  
  // Configuration Flags
  autoUpdateTimesheet: boolean;
  doNotUpdateTimesheetWithHours: boolean;
  obeyUserWorkingProfileAndBankHolidays: boolean;
  autoAdjustBookingForWorkingProfileAndBankHolidays: boolean;
  addDaysToEndOfBooking: boolean;
  allowRepeatBookings: boolean;
  requiresApproval: boolean;     // Requires manager approval
  allowOverlapping: boolean;     // Allow overlapping bookings
  
  // Field Configurations
  fieldConfigurations: FieldConfiguration[];
  fieldOrder: string[];
  leftColumnFields?: string[];
  rightColumnFields?: string[];
  tabConfigurations?: TabConfiguration[];
  
  // Color & Display
  color: string;                 // Hex color code
  icon?: string;                 // Icon identifier (lucide-react icon name)
  
  // Permissions
  allowedRoles: WorkspaceRole[]; // Roles that can use this type
  restrictedResources?: string[]; // Resource IDs that cannot use this type
  
  // Audit Fields
  createdAt: string;             // ISO timestamp
  createdBy: string;             // User ID
  updatedAt: string;             // ISO timestamp
  updatedBy: string;             // User ID
  version: number;               // Version number for optimistic locking
  
  // Session Metadata
  sessionId?: string;            // Session identifier
  isTemporary: boolean;          // Is this a temporary/draft type?
}

// ============================================================================
// BOOKINGS TABLE
// ============================================================================

/**
 * Attachment Record
 * 
 * Represents a file attached to a booking
 */
export interface AttachmentRecord {
  id: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;              // Bytes
  mimeType: string;
  uploadedAt: string;            // ISO timestamp
  uploadedBy: string;            // User ID
}

/**
 * Booking Record
 * 
 * Stores individual booking records with full details
 */
export interface BookingRecord {
  // Primary Key
  id: string;                    // UUID format: 'booking-uuid-xxxxx'
  
  // Foreign Keys
  bookingTypeId: string;         // FK to BookingTypes.id
  resourceId: string;            // Primary resource (FK to Resources.id)
  resourceIds?: string[];        // Multiple resources support
  projectId: string;             // FK to Projects.id
  costCentreId?: string;         // FK to CostCentres.id (optional)
  clientId?: string;             // FK to Clients.id (optional)
  
  // Core Booking Details
  title: string;                 // Booking title/summary
  description?: string;          // Detailed description
  projectName: string;           // Denormalized for performance
  
  // Date & Time
  startTime: string;             // ISO timestamp
  endTime: string;               // ISO timestamp
  timezone: string;              // IANA timezone (e.g., 'America/New_York')
  allDay: boolean;               // Is this an all-day booking?
  
  // Duration
  durationType: DurationType;
  halfDayPeriod?: HalfDayPeriod;
  durationHours: number;         // Calculated duration in hours
  durationDays: number;          // Calculated duration in days
  
  // Recurrence
  recurrence?: RecurrenceSettings;
  parentBookingId?: string;      // FK to parent booking (for recurring)
  recurrenceInstanceDate?: string; // Date of this instance
  
  // Classification
  billingClassification: BillingClassification;
  workType: WorkType;
  status: BookingStatus;
  priority: PriorityLevel;
  
  // Additional Details
  taskDescription?: string;
  bookingNotes?: string;
  internalNotes?: string;        // Internal notes (not visible to client)
  attachments?: AttachmentRecord[];
  tags?: string[];               // Tags for categorization
  
  // Notifications & Reminders
  notifyReminders: boolean;
  reminderMinutes?: number[];    // Minutes before start to send reminders
  notifiedUsers?: string[];      // User IDs who have been notified
  
  // Approval Workflow
  requiresApproval: boolean;
  approvalStatus?: ApprovalStatus;
  approvedBy?: string;           // User ID
  approvedAt?: string;           // ISO timestamp
  rejectionReason?: string;
  
  // Financial
  estimatedCost?: number;
  actualCost?: number;
  billableHours?: number;
  hourlyRate?: number;
  
  // Display
  color: string;                 // Hex color code (inherited or custom)
  
  // Audit Fields
  createdAt: string;             // ISO timestamp
  createdBy: string;             // User ID
  updatedAt: string;             // ISO timestamp
  updatedBy: string;             // User ID
  version: number;               // Version number for optimistic locking
  
  // Session Metadata
  sessionId?: string;            // Session identifier
  isDraft: boolean;              // Is this a draft booking?
  isDeleted: boolean;            // Soft delete flag
  deletedAt?: string;            // ISO timestamp
  deletedBy?: string;            // User ID
}

// ============================================================================
// BOOKING HISTORY TABLE
// ============================================================================

/**
 * Changed Field Record
 * 
 * Represents a single field change in the history
 */
export interface ChangedField {
  fieldName: string;
  oldValue: any;
  newValue: any;
  dataType: string;
}

/**
 * Booking History Record
 * 
 * Tracks all changes to bookings for audit trail
 */
export interface BookingHistoryRecord {
  // Primary Key
  id: string;                    // UUID format: 'history-uuid-xxxxx'
  
  // Foreign Key
  bookingId: string;             // FK to Bookings.id
  
  // Change Details
  changeType: ChangeType;
  changedFields: ChangedField[];
  previousState?: Partial<BookingRecord>;
  newState?: Partial<BookingRecord>;
  
  // Change Metadata
  changeReason?: string;
  changeNotes?: string;
  
  // Audit Fields
  changedAt: string;             // ISO timestamp
  changedBy: string;             // User ID
  changedByName: string;         // User name (denormalized)
  ipAddress?: string;            // IP address of change
  userAgent?: string;            // Browser/client info
  
  // Session Metadata
  sessionId?: string;            // Session identifier
}

// ============================================================================
// BOOKING CONFLICTS TABLE
// ============================================================================

/**
 * Booking Conflict Record
 * 
 * Tracks and manages booking conflicts
 */
export interface BookingConflictRecord {
  // Primary Key
  id: string;                    // UUID format: 'conflict-uuid-xxxxx'
  
  // Foreign Keys
  bookingId1: string;            // FK to Bookings.id (first booking)
  bookingId2: string;            // FK to Bookings.id (conflicting booking)
  resourceId: string;            // FK to Resources.id (conflicted resource)
  
  // Conflict Details
  conflictType: ConflictType;
  conflictSeverity: ConflictSeverity;
  overlapStartTime: string;      // ISO timestamp
  overlapEndTime: string;        // ISO timestamp
  overlapDurationMinutes: number;
  
  // Resolution
  isResolved: boolean;
  resolutionType?: ResolutionType;
  resolvedAt?: string;           // ISO timestamp
  resolvedBy?: string;           // User ID
  resolutionNotes?: string;
  
  // Audit Fields
  detectedAt: string;            // ISO timestamp
  detectedBy: string;            // System or User ID
  
  // Session Metadata
  sessionId?: string;            // Session identifier
}

// ============================================================================
// BOOKING TEMPLATES TABLE
// ============================================================================

/**
 * Booking Template Record
 * 
 * Stores reusable booking templates
 */
export interface BookingTemplateRecord {
  // Primary Key
  id: string;                    // UUID format: 'template-uuid-xxxxx'
  
  // Template Details
  name: string;
  description?: string;
  category: BookingCategory;
  
  // Template Configuration (same as BookingRecord but without IDs)
  bookingTypeId: string;
  templateData: Partial<BookingRecord>;
  
  // Usage
  isPublic: boolean;             // Available to all users
  createdBy: string;             // User ID (owner)
  usageCount: number;            // How many times used
  lastUsedAt?: string;           // ISO timestamp
  
  // Audit Fields
  createdAt: string;             // ISO timestamp
  updatedAt: string;             // ISO timestamp
  
  // Session Metadata
  sessionId?: string;            // Session identifier
}

// ============================================================================
// METADATA & UTILITY TYPES
// ============================================================================

/**
 * Booking Metadata
 * 
 * Stores metadata about the booking system state
 */
export interface BookingMetadata {
  version: string;               // Schema version
  lastSyncAt: string;            // Last sync timestamp
  totalBookings: number;
  totalBookingTypes: number;
  sessionId: string;             // Current session ID
  userId: string;                // Current user ID
  createdAt: string;             // Session creation time
}

/**
 * Validation Result
 * 
 * Result of data validation
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

/**
 * Storage Keys
 * 
 * Keys used for sessionStorage
 */
export const STORAGE_KEYS = {
  BOOKING_TYPES: 'booking_types_v1',
  BOOKINGS: 'bookings_v1',
  BOOKING_HISTORY: 'booking_history_v1',
  BOOKING_CONFLICTS: 'booking_conflicts_v1',
  BOOKING_TEMPLATES: 'booking_templates_v1',
  METADATA: 'booking_metadata_v1',
} as const;

// ============================================================================
// QUERY & FILTER TYPES
// ============================================================================

/**
 * Booking Query Options
 * 
 * Options for querying bookings
 */
export interface BookingQueryOptions {
  // Filters
  bookingTypeIds?: string[];
  resourceIds?: string[];
  projectIds?: string[];
  statuses?: BookingStatus[];
  priorities?: PriorityLevel[];
  categories?: BookingCategory[];
  
  // Date Range
  startDate?: string;            // ISO date
  endDate?: string;              // ISO date
  
  // Flags
  includeDrafts?: boolean;
  includeDeleted?: boolean;
  includeRecurring?: boolean;
  
  // Pagination
  page?: number;
  pageSize?: number;
  
  // Sorting
  sortBy?: keyof BookingRecord;
  sortOrder?: 'asc' | 'desc';
  
  // Search
  searchTerm?: string;
  searchFields?: (keyof BookingRecord)[];
}

/**
 * Booking Type Query Options
 * 
 * Options for querying booking types
 */
export interface BookingTypeQueryOptions {
  // Filters
  categories?: BookingCategory[];
  activeOnly?: boolean;
  
  // Sorting
  sortBy?: keyof BookingTypeRecord;
  sortOrder?: 'asc' | 'desc';
  
  // Search
  searchTerm?: string;
}

/**
 * Query Result
 * 
 * Result of a query operation
 */
export interface QueryResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// ============================================================================
// CRUD OPERATION TYPES
// ============================================================================

/**
 * Create Booking Type Input
 */
export type CreateBookingTypeInput = Omit<
  BookingTypeRecord, 
  'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'version'
>;

/**
 * Update Booking Type Input
 */
export type UpdateBookingTypeInput = Partial<CreateBookingTypeInput> & {
  id: string;
  version: number; // For optimistic locking
};

/**
 * Create Booking Input
 */
export type CreateBookingInput = Omit<
  BookingRecord, 
  'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'version' | 'durationHours' | 'durationDays'
>;

/**
 * Update Booking Input
 */
export type UpdateBookingInput = Partial<CreateBookingInput> & {
  id: string;
  version: number; // For optimistic locking
};

// ============================================================================
// EXPORT ALL TYPES
// ============================================================================
// Types are already exported above with their definitions
